import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../../app/store/hooks';
import type { ChatMessage, ChatRoomSummary } from '../../application/types';
import {
  fetchChatMessages,
  sendChatMessage,
} from '../../infrastructure/api/chat.api';

export function useChatMessages(selectedRoom: ChatRoomSummary | null) {
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const currentUserId = useAppSelector((state) =>
    state.authorization.user?.id != null
      ? String(state.authorization.user.id)
      : '',
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!selectedRoom?.id || !accessToken) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchChatMessages(selectedRoom.id, accessToken)
      .then((list) => {
        if (!cancelled) {
          setMessages(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMessages([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRoom?.id, accessToken]);

  const send = useCallback(
    async (body: string) => {
      if (!selectedRoom || !accessToken || !currentUserId || !body.trim()) {
        return;
      }

      const toId = selectedRoom.participants.find((id) => id !== currentUserId);
      if (!toId) {
        return;
      }

      setIsSending(true);
      try {
        const created = await sendChatMessage(
          {
            chatRoomId: selectedRoom.id,
            body: body.trim(),
            toId,
            ownerId: currentUserId,
          },
          accessToken,
        );
        setMessages((prev) => [...prev, created]);
      } finally {
        setIsSending(false);
      }
    },
    [selectedRoom, accessToken, currentUserId],
  );

  return { messages, isLoading, isSending, send, currentUserId };
}
